import React from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Card, Accordion, Form, InputGroup } from 'react-bootstrap';
import { Field, ErrorMessage } from 'formik';

import { FormColorInput } from '@components/Common/AwpFormInputs';

import * as Helper from '../Helpers/PresetHelper';
import * as PresetButtons from '../Helpers/GraphPresetButtons';
import * as Actions from '../Helpers/Actions';

import Select, { components } from 'react-select';

/**
 * Custom option component.
 *
 * @param {object} props - Props for the Option component.
 * @returns {JSX.Element} Rendered Option component.
 */
// Define the Option component using ES6 destructuring for props
export const SingleValue = (props) => (
  <components.SingleValue {...props}>
    {`${props.data.label}`}
    {props.data.placementName ? <i>{` [${props.data.placementName ? props.data.placementName : ''}]`}</i> : null}
  </components.SingleValue>
);

/**
 * A component for rendering and managing observation data related to a specific data graph.
 *
 * @component
 * @param {Object} data - The data associated with the current data graph.
 * @param {Array<Object>} observationData - An array of observation data options.
 * @param {number} weatherStationId - The ID of the associated weather station.
 * @param {number} displayOrder - The display order of the data graph.
 * @param {Function} setWXDropdownDisabled - A function to set the dropdown's disabled state.
 * @param {boolean} isLoading - A flag indicating whether data is currently being loaded.
 * @param {Function} ref - A reference to the component.
 * @returns {React.Component} The rendered ObservationData component.
 */
export const ObservationDataItem = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();

  const startingZIndex = 1000;

  // Styles for the Select dropdown component
  const selectStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: 'white',
      borderColor: '#adb5bd',
      color: '#494949',
    }),
    container: (styles) => ({ ...styles, width: '100%', zIndex: `${startingZIndex}` }),
    input: (styles) => ({ ...styles }),
    placeholder: (styles) => ({ ...styles, color: '#494949' }),
    dropdownIndicator: (styles) => ({ ...styles, color: '#797979', '&:hover': { color: '#494949' } }),
  };

  const controlStylesHasError = (hasError) => (hasError ? { background: 'red' } : {});
  // Render each persistent graph filter associated with the data graph
  return props.stationData.PersistentGraphFilters[props.weatherStationId].map((pgf, idx) => {
    let graphFilter = pgf;

    let filteredObservationData = props.observationData;
    const isWeatherStation = props.weatherStationId !== 'null' && parseInt(props.weatherStationId || -1) > -1;

    //need to add the datasetPrefix value to the PGF, it's not stored in the database but we can find it, in the Observations
    graphFilter = {
      ...graphFilter,
      DatasetPrefix:
        props.observationData.flatMap((obj) => obj.options).find((o) => o?.label === graphFilter.PlacementName)?.object
          .DatasetPrefix || '0',
    };

    const selectedOTs = props.stationData.PersistentGraphFilters[props.weatherStationId]
      .map((p) => {
        if (p.ObservationTypeId >= 0) {
          return { ObservationTypeId: p.ObservationTypeId, PlacementName: p.PlacementName };
        }
        return null;
      })
      .filter((value) => value !== null);

    if (!isWeatherStation) {
      // Handle observations with no grouping (deferred options)
      filteredObservationData = filteredObservationData.filter((o) => {
        return !selectedOTs.some((selectedOT) => selectedOT.ObservationTypeId === o.id);
      });
    } else {
      // Handle observations that are grouped (weather station selected)
      filteredObservationData = filteredObservationData.map((od) => {
        return {
          ...od,
          options: od.options?.filter((o) => {
            const isCamera = o.object.DatasetPrefix === '1'; // Check if it's a camera

            return !selectedOTs.some(
              (selectedOT) =>
                selectedOT.ObservationTypeId === o.id &&
                ((isCamera && selectedOT.PlacementName === o.object.ObservationTypeLabel) ||
                  (!isCamera && selectedOT.PlacementName === o.placementName)),
            );
          }),
        };
      });
    }

    //need to find the actual item within the main data object using the unmodified pgf
    const pgfIndex = props.data.PersistentGraphFilters.findIndex((f) => f === pgf);

    // Find the selected observation option
    const selectedOption =
      Helper.findSelectedOption(
        props.observationData,
        graphFilter.ObservationTypeId,
        graphFilter.PlacementName,
        graphFilter.DatasetPrefix,
      ) || null;

    // Disable the weather station dropdown if an observation type is selected
    props.setWXDropdownDisabled(selectedOTs.length > 0 ? true : false);
    const fieldName = `DataGraphs[${props.dataGraphIndex}].PersistentGraphFilters[${pgfIndex}].ObservationTypeId`;

    const isTouched =
      props.formik.touched.DataGraphs?.[props.dataGraphIndex]?.PersistentGraphFilters?.[pgfIndex]?.ObservationTypeId ||
      false;

    const isError =
      props.formik.errors.DataGraphs?.[props.dataGraphIndex]?.PersistentGraphFilters?.[pgfIndex]?.ObservationTypeId ||
      false;

    return (
      <div key={`${props.stationData.DataGraphId}_${idx}`}>
        <Row>
          <Col sm={5} md={5} lg={5}>
            <Form.Group className="my-1">
              <Form.Label className="flex-grow-1">
                <b>{isWeatherStation ? 'Dataset' : 'Observation Type'}</b>
              </Form.Label>
              {/* TODO: Observation Dataset Name is display only when Read Only */}
              <InputGroup className="mb-3">
                <Field name={fieldName}>
                  {({ field }) => (
                    <Select
                      key={`select_${props.stationData.DataGraphId}_${idx}`}
                      components={{ SingleValue }}
                      isLoading={props.isLoading}
                      styles={{
                        ...selectStyles,
                        control: (styles) => ({
                          ...styles,
                          backgroundColor: 'white',
                          border: isError ? '2px solid red' : '1px solid rgba(0, 0, 0, 0.3)',
                          color: '#494949',
                          '&:hover': {
                            border: isError ? '2px solid red' : '1px solid rgba(0, 0, 0, 0.3)',
                          },
                        }),
                        container: (styles) => ({
                          width: '100%',
                          zIndex: `${startingZIndex - (idx + 1)}`,
                        }),
                      }}
                      options={filteredObservationData}
                      value={selectedOption}
                      getOptionValue={(option) => option.value} // Use the unique identifier as the value
                      getOptionLabel={(option) => option.label}
                      onChange={(e) => {
                        //console.log(e);
                        dispatch(
                          Actions.updateGraphFilterTypeId(
                            props.stationData.DataGraphPresetId,
                            props.weatherStationId,
                            props.displayOrder,
                            e.placementName || null,
                            graphFilter.PlacementName,
                            graphFilter.ObservationTypeId,
                            e.id,
                            e.object,
                            props.formik,
                          ),
                        );
                        props.setWXDropdownDisabled(true);
                      }}
                      isDisabled={props.readOnly}
                    />
                    // </div>
                  )}
                </Field>
                <ErrorMessage name={fieldName} />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col sm={6} md={6} lg={5}>
            {/* TODO: Color doesn't reset when item is deleted
            color doesn't display when field is disabled/read-only */}
            <FormColorInput
              label="Colour"
              name={`DataGraphs[${props.dataGraphIndex}].PersistentGraphFilters[${pgfIndex}].ColorCodeOverride`}
              id={`DataGraphs[${props.dataGraphIndex}].PersistentGraphFilters[${pgfIndex}].ColorCodeOverride`}
              readOnly={props.readOnly}
              disabled={props.readOnly}
              hasLightnessCheck={false}
              postChange={(colorValue) => {
                dispatch(
                  Actions.updateGraphFilterColorOverride(
                    props.stationData.DataGraphPresetId,
                    props.weatherStationId,
                    props.displayOrder,
                    graphFilter.ObservationTypeId,
                    graphFilter.DatasetPrefix,
                    graphFilter.PlacementName,
                    colorValue,
                    props.formik,
                  ),
                );
              }}
            />
          </Col>
          <Col sm={1} md={1} lg={2} className="pl-0">
            {props.readOnly ? null : (
              <PresetButtons.GraphFilterButtons
                displayOrder={props.displayOrder}
                graphPresetId={props.data.DataGraphPresetId}
                weatherStationId={props.weatherStationId}
                placementName={graphFilter.PlacementName}
                typeId={graphFilter.ObservationTypeId}
                formik={props.formik}
              />
            )}
          </Col>
        </Row>
      </div>
    );
  });
});

// Display name for the component
ObservationDataItem.displayName = 'ObservationData';
